import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import PDFPreviewFallback from '../components/PDFPreviewFallback';
import { t } from '../i18n';

const apiUrl = process.env.REACT_APP_API_URL;

const PreviewPage = () => {
  const { filename, id, type, pdfFilename } = useParams();
  const [pdfUrl, setPdfUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [documentInfo, setDocumentInfo] = useState({ type: '', number: '', id: '' });

  // Determine which URL structure we're using
  const isNewUrlStructure = id && type && pdfFilename;
  const isIdOnlyStructure = id && !type && !pdfFilename && !filename; // New structure: /delivery-notes/preview/:id or /invoices/preview/:id
  
  useEffect(() => {
    const fetchPdf = async () => {
      try {
        setLoading(true);
        setError('');

        let documentType = '';
        let documentNumber = '';
        let documentId = '';

        if (isIdOnlyStructure) {
          // New URL structure: /delivery-notes/preview/:id or /invoices/preview/:id
          const currentPath = window.location.pathname;
          
          if (currentPath.includes('/delivery-notes/preview/')) {
            documentType = 'dnote';
            documentId = id;
          } else if (currentPath.includes('/invoices/preview/')) {
            documentType = 'invoice';
            documentId = id;
          } else {
            throw new Error('Invalid preview URL structure');
          }

          console.log('PreviewPage ID-only Debug:', { documentId, documentType });

          // Fetch document data from database to get PDF filename
          const token = localStorage.getItem('token');
          if (!token) {
            throw new Error('Authentication token not found');
          }

          const endpoint = documentType === 'dnote' ? `dnotes` : `invoices`;
          
          // First get all documents to find the one with matching ID
          const response = await fetch(`${apiUrl}/${endpoint}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });

          if (!response.ok) {
            throw new Error(`Failed to fetch ${documentType} data: ${response.status} ${response.statusText}`);
          }

          const result = await response.json();
          const documents = Array.isArray(result.data) ? result.data : Array.isArray(result) ? result : [];
          const document = documents.find(doc => doc.id === parseInt(documentId));
          
          if (!document) {
            throw new Error(`${documentType} with ID ${documentId} not found`);
          }

          if (!document.pdf_filename) {
            throw new Error(`No PDF file available for this ${documentType}`);
          }

          // Set document info
          const numberField = documentType === 'dnote' ? 'dnote_number' : 'invoice_number';
          setDocumentInfo({ 
            type: documentType, 
            number: document[numberField] || '', 
            id: documentId 
          });

          // Fetch PDF file using download-pdf endpoint
          const pdfResponse = await fetch(`${apiUrl}/download-pdf/${encodeURIComponent(document.pdf_filename)}?document_type=${documentType}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });

          if (!pdfResponse.ok) {
            throw new Error(`Failed to fetch PDF file: ${pdfResponse.status} ${pdfResponse.statusText}`);
          }

          // Create a blob URL for the PDF
          const blob = await pdfResponse.blob();
          const url = URL.createObjectURL(blob);
          setPdfUrl(url);
          
        } else if (isNewUrlStructure) {
          // New URL structure: /preview/:id/:type/:pdfFilename
          const companyId = id; // Using id as companyId
          documentType = type === 'invoices' ? 'invoice' : 'delivery-note';
          
          console.log('PreviewPage Debug:', { companyId, type, pdfFilename, documentType });
          
          // Extract document number from PDF filename
          const decodedPdfFilename = decodeURIComponent(pdfFilename);
          console.log('Decoded filename:', decodedPdfFilename);
          
          if (decodedPdfFilename.includes('Фактура#') || decodedPdfFilename.includes('Фактура-')) {
            // Handle both old (#) and new (-) filename formats
            const separator = decodedPdfFilename.includes('Фактура#') ? 'Фактура#' : 'Фактура-';
            documentNumber = decodedPdfFilename.split(separator)[1]?.split('_')[0] || '';
          } else if (decodedPdfFilename.includes('Испратница#') || decodedPdfFilename.includes('Испратница-')) {
            // Handle both old (#) and new (-) filename formats  
            const separator = decodedPdfFilename.includes('Испратница#') ? 'Испратница#' : 'Испратница-';
            documentNumber = decodedPdfFilename.split(separator)[1]?.split('_')[0] || '';
          }
          
          setDocumentInfo({ type: documentType, number: documentNumber, id: companyId });
          
          // Fetch PDF directly from storage using company/folder/filename structure
          const folderName = type; // 'invoices' or 'dnote'
          const apiUrl_with_path = `${apiUrl}/storage/${companyId}/${folderName}/${encodeURIComponent(pdfFilename)}`;
          console.log('Fetching PDF from:', apiUrl_with_path);
          
          const response = await fetch(apiUrl_with_path, {
            method: 'GET',
            headers: {
              'Accept': 'application/pdf',
            },
          });

          console.log('Response status:', response.status, response.statusText);

          if (!response.ok) {
            throw new Error(`Failed to fetch PDF: ${response.status} ${response.statusText}`);
          }

          // Create a blob URL for the PDF
          const blob = await response.blob();
          const url = URL.createObjectURL(blob);
          setPdfUrl(url);
          
        } else {
          // Original URL structure: /preview/:filename
          const decodedFilename = decodeURIComponent(filename);
          
          if (decodedFilename.startsWith('delivery-note-')) {
            documentType = 'delivery-note';
            documentNumber = decodedFilename.replace('delivery-note-', '').replace('.pdf', '');
          } else if (decodedFilename.startsWith('invoice-')) {
            documentType = 'invoice';
            documentNumber = decodedFilename.replace('invoice-', '').replace('.pdf', '');
          } else {
            throw new Error('Invalid document type');
          }

          // Store document info for fallback
          setDocumentInfo({ type: documentType, number: documentNumber, id: '' });

          // First, get the document ID from the database using the document number
          const searchEndpoint = documentType === 'delivery-note' ? 'dnotes' : 'invoices';
          const searchResponse = await fetch(`${apiUrl}/${searchEndpoint}`, {
            method: 'GET',
          });

          if (!searchResponse.ok) {
            throw new Error(`Failed to search documents: ${searchResponse.status}`);
          }

          const documents = await searchResponse.json();
          const numberField = documentType === 'delivery-note' ? 'dnote_number' : 'invoice_number';
          const document = documents.find(doc => doc[numberField] === documentNumber);
          
          if (!document) {
            throw new Error(`Document with number ${documentNumber} not found`);
          }

          documentId = document.id;

          // Now fetch the PDF using the document ID
          const endpoint = documentType === 'delivery-note' ? 'dnote-pdf' : 'invoice-pdf';
          const response = await fetch(`${apiUrl}/${endpoint}/${documentId}`, {
            method: 'GET',
            headers: {
              'Accept': 'application/pdf',
            },
          });

          if (!response.ok) {
            throw new Error(`Failed to fetch PDF: ${response.status} ${response.statusText}`);
          }

          // Create a blob URL for the PDF
          const blob = await response.blob();
          const url = URL.createObjectURL(blob);
          setPdfUrl(url);
        }
      } catch (err) {
        console.error('Error fetching PDF:', err);
        setError(err.message || t('Неуспешно вчитување на PDF'));
      } finally {
        setLoading(false);
      }
    };

    if ((isNewUrlStructure && id && type && pdfFilename) || (!isNewUrlStructure && filename)) {
      fetchPdf();
    }

    // Cleanup blob URL on unmount
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [filename, id, type, pdfFilename, isNewUrlStructure]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-lg text-gray-600">{t('Вчитување PDF...')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100">
        {/* Fallback content */}
        <PDFPreviewFallback 
          filename={decodeURIComponent(filename)}
          documentType={documentInfo.type}
          documentNumber={documentInfo.number}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      {/* PDF Viewer */}
      <div className="h-screen">
        {pdfUrl ? (
          <iframe
            src={pdfUrl}
            className="w-full h-full border-0"
            title={t('Преглед на PDF')}
            style={{ height: '100vh' }}
          />
        ) : (
          <div className="flex items-center justify-center h-full">
            <p className="text-white text-lg">{t('Нема PDF за прикажување')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PreviewPage;